import { Export } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "react-toastify";

import { INTEGRATIONS_MAP } from "features/integrations/constants";
import useIntegrationSettings from "features/integrations/datahooks/useIntegrationSettings";
import type {
  CrmExportType,
  IntegrationSettings,
  OutreachSourceType,
} from "features/integrations/types";

import { SplitButton } from "common/components/SplitButton";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { get } from "common/helpers/HTTP";
import { formatNumber } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

import useLeadExports from "../../../datahooks/useLeadExports";
import CrmConfirmationModal from "./CrmConfirmationModal";
import OutreachExportModal from "./OutreachExportModal";

import EmailBisonIcon from "assets/icons/integrations/emailBison-simple.svg?react";
import HubspotIcon from "assets/icons/integrations/hubspot-simple.svg?react";
import InstantlyIcon from "assets/icons/integrations/instantly-simple.svg?react";
import PipedriveIcon from "assets/icons/integrations/pipedrive-simple.svg?react";
import SmartleadIcon from "assets/icons/integrations/smartlead-simple.svg?react";

const crmIntegrationExports = [
  { key: "hubspot", icon: HubspotIcon },
  { key: "pipedrive", icon: PipedriveIcon },
  { key: "emailbison", icon: EmailBisonIcon },
] as const;

const outreachIntegrationsExport = [
  { key: "instantly", icon: InstantlyIcon },
  { key: "smartlead", icon: SmartleadIcon },
] as const;

function isInstalledCrmExportIntegration(
  settings: IntegrationSettings[CrmExportType],
) {
  return !!settings && !("error" in settings) && !("install_url" in settings);
}

async function pollExportJob(
  workspaceId: string,
  jobId: string,
  integrationName: string,
) {
  const { job } = await get(
    `workspaces/${workspaceId}/integrations/jobs/${jobId}`,
  );
  if (job.state === "in_progress") {
    setTimeout(() => pollExportJob(workspaceId, jobId, integrationName), 8000);
  } else if (job.state === "success") {
    toast.success(`Data exported to ${integrationName} successfully`);
  } else if (job.state === "failed") {
    toast.error(
      `Data export to ${integrationName} failed with code ${job.code}`,
    );
  }
}

interface ExportLeadsProps {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  totalLeadsCount: number;
  isFetching: boolean;
}

export default function ExportLeads({
  searchParams,
  totalLeadsCount,
  isFetching,
}: ExportLeadsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [exportUrl, setExportUrl] = useState<string>();
  const [selectedOutreachType, setSelectedOutreachType] =
    useState<OutreachSourceType>();
  const [selectedCrmExportType, setSelectedCrmExportType] =
    useState<CrmExportType>();

  const { integrationSettings } = useIntegrationSettings();

  const isDisabled = isFetching || totalLeadsCount === 0;

  const hasConnectedOutreachIntegrations =
    integrationSettings &&
    outreachIntegrationsExport.some(({ key }) => !!integrationSettings[key]);

  const hasInstalledCrmIntegrations =
    integrationSettings &&
    crmIntegrationExports.some(({ key }) =>
      isInstalledCrmExportIntegration(integrationSettings[key]),
    );

  const { exportLeads, isExportingLeads, isExportingLeadsToCrmIntegration } =
    useLeadExports();

  function onExport() {
    exportLeads({ searchParams }).then((url) => {
      setExportUrl(url);
    });

    toast("Your export is preparing, you will be notified when it's ready");
  }

  const commonButtonProps = {
    variant: "secondary-purple",
    leftIcon: <Export weight="fill" />,
    disabled: isDisabled,
    onClick: onExport,
    isLoading: isExportingLeads,
    size: isDesktop ? "sm" : "md",
    className: "only:flex-1",
    children: (
      <>
        Export{" "}
        {isFetching ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          `(${formatNumber(totalLeadsCount)})`
        )}
      </>
    ),
  } as const;

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      {hasConnectedOutreachIntegrations || hasInstalledCrmIntegrations ? (
        <SplitButton
          {...commonButtonProps}
          dropdownMenuItems={[
            ...crmIntegrationExports
              .filter(({ key }) =>
                isInstalledCrmExportIntegration(integrationSettings[key]),
              )
              .map(({ key, icon }) => {
                const title = INTEGRATIONS_MAP[key].title;

                return {
                  label: `Export to ${title}`,
                  icon,
                  onSelect: () => {
                    setIsConfirmationModalOpen(true);
                    setSelectedCrmExportType(key);
                  },
                  disabled: isExportingLeadsToCrmIntegration,
                };
              }),
            ...outreachIntegrationsExport
              .filter(({ key }) => integrationSettings[key])
              .map(({ key, icon }) => ({
                label: `Export to ${INTEGRATIONS_MAP[key].title}`,
                icon,
                onSelect: () => {
                  setSelectedOutreachType(key);
                  setIsModalOpen(true);
                },
              })),
          ]}
        />
      ) : (
        <Button {...commonButtonProps} />
      )}

      {hasConnectedOutreachIntegrations && (
        <OutreachExportModal
          key={selectedOutreachType}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          type={selectedOutreachType}
          searchParams={searchParams}
        />
      )}

      {hasInstalledCrmIntegrations && selectedCrmExportType && (
        <CrmConfirmationModal
          type={selectedCrmExportType}
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          searchParams={searchParams}
          onAsyncJobStarted={(jobId, name) => {
            pollExportJob(workspaceId, jobId, name);
            toast(
              "Your export is preparing, you will be notified when it's ready",
            );
          }}
        />
      )}

      <Component
        open={!!exportUrl}
        onOpenChange={(open) => {
          if (!open) {
            setExportUrl(undefined);
          }
        }}
      >
        <Content>
          <Header
            title="Your export is ready"
            subtitle="Click the button below to download your export in CSV format"
          />

          <Footer className="justify-end">
            <Button
              onClick={() => {
                window.open(exportUrl);
                setExportUrl(null);
              }}
            >
              Download
            </Button>
          </Footer>
        </Content>
      </Component>
    </>
  );
}
